import React, { ReactText, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';

import { ThemeProvider, ThemeProviderProps } from '@common/styles/theme-provider';

import { BottomDialog, BottomDialogOption } from './dump/BottomDialog';

export function useBottomDialog(color_scheme?: ThemeProviderProps['colorScheme']) {
  const queryClientRef = useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    }),
  );

  const awaitingPromiseRef = React.useRef<{
    resolve: (id: ReactText) => void;
    reject: () => void;
  }>();
  const [option, setOption] = useState<BottomDialogOption>();

  const handleSubmit = useCallback(
    (id: ReactText) => {
      awaitingPromiseRef.current?.resolve(id);
      setOption(undefined);
    },
    [setOption],
  );

  const buttons = useMemo(() => {
    return option?.buttons?.map((value, index) => ({
      ...value,
      onClick: () => {
        value.onClick?.();
        handleSubmit(value.id ?? index);
      },
    }));
  }, [handleSubmit, option?.buttons]);

  const wrapper = useMemo(
    () => (typeof document !== 'undefined' ? document.body.appendChild(document.createElement('div')) : null),
    [],
  );

  const dispose = useCallback(() => {
    if (!wrapper) {
      return;
    }
    setTimeout(() => {
      unmountComponentAtNode(wrapper);
      setTimeout(() => {
        if (document.body.contains(wrapper)) {
          document.body.removeChild(wrapper);
        }
      });
    });
  }, [wrapper]);

  const handleOutsideClick = useCallback(() => {
    if (option?.disableBackdropClick) {
      return;
    }
    if (option?.canBackDropResolve) {
      awaitingPromiseRef.current?.resolve('skip');
    }
    setOption(undefined);
  }, [option?.canBackDropResolve, option?.disableBackdropClick]);

  const active = useMemo(() => {
    return Boolean(option);
  }, [option]);

  const dialog = useMemo(() => {
    if (color_scheme) {
      return (
        <ThemeProvider colorScheme={color_scheme}>
          <QueryClientProvider client={queryClientRef.current}>
            <BottomDialog {...{ ...option, active, handleOutsideClick, buttons }} />
          </QueryClientProvider>
        </ThemeProvider>
      );
    }

    return (
      <QueryClientProvider client={queryClientRef.current}>
        <BottomDialog {...{ ...option, active, handleOutsideClick, buttons }} />
      </QueryClientProvider>
    );
  }, [option, active, buttons, handleOutsideClick, color_scheme]);

  const open = (open_option: BottomDialogOption) => {
    setOption(open_option);
    return new Promise((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const close = () => {
    setOption(undefined);
  };

  useEffect(() => {
    render(dialog, wrapper);
  }, [dialog, wrapper]);
  useEffect(() => {
    return () => {
      dispose();
    };
  }, [dispose]);
  return { open, close };
}
